/**
 * Margins — what we billed against what it cost us, for management to see and adjust pricing (Jason, 2026-10-26).
 * NMVTIS against AAMVA's charge, MVR by state against each state's fee, and the AAMVA schedule those use.
 * Same access as Pricing Management.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { getErrorMessage } from '../services/apiService';
import { MvrMarginReport, NmvtisMarginReport, getMvrMargins, getNmvtisMargins } from '../services/marginsService';
import NmvtisMarginPanel from '../components/admin/margins/NmvtisMarginPanel';
import MvrStateMarginPanel from '../components/admin/margins/MvrStateMarginPanel';
import AamvaSchedulePanel from '../components/admin/margins/AamvaSchedulePanel';

// Billing months are UTC calendar months.
const lastTwelveMonths = () => {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) + (i === 0 ? ' (so far)' : ''),
    };
  });
};

const Margins: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const months = useMemo(lastTwelveMonths, []);
  const [selected, setSelected] = useState(0);
  const [nmvtis, setNmvtis] = useState<NmvtisMarginReport | null>(null);
  const [mvr, setMvr] = useState<MvrMarginReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!user.hasPricingAccess) navigate('/settings');
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    const { year, month } = months[selected];
    try {
      setLoading(true);
      setError('');
      const [n, m] = await Promise.all([getNmvtisMargins(year, month), getMvrMargins(year, month)]);
      setNmvtis(n);
      setMvr(m);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [months, selected]);

  useEffect(() => {
    if (user?.hasPricingAccess) load();
  }, [user, load]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="pt-28 md:pt-32 lg:ml-[220px] flex-1">
        <div className="relative z-20 px-4 md:px-8 py-6">
          <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <TrendingUp className="w-8 h-8 text-[#c41e3a]" />
                <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a]">Margins</h1>
              </div>
              <p className="text-[#64748b] text-lg">What we billed against what it cost us</p>
            </div>
            <label className="text-xs text-[#64748b]">
              Month
              <select
                value={selected}
                onChange={(e) => setSelected(parseInt(e.target.value, 10))}
                className="block mt-1 px-3 py-2 rounded-lg border border-black/[0.12] bg-white text-sm text-[#0f172a]"
              >
                {months.map((m, i) => <option key={`${m.year}-${m.month}`} value={i}>{m.label}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="relative z-10 px-4 md:px-8 pb-8">
          <div className="max-w-6xl mx-auto space-y-6">
            {error && <div className="p-4 rounded-xl border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}

            {loading && !nmvtis ? (
              <div className="py-16 text-center text-[#64748b]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
            ) : (
              <>
                {nmvtis && <NmvtisMarginPanel report={nmvtis} onPricesApplied={load} />}
                {mvr && <MvrStateMarginPanel report={mvr} onChanged={load} />}
                <AamvaSchedulePanel onSaved={load} />
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Margins;
